
import { useState } from 'react';
import { Job } from '@/types/job';

// Update the JobFilters interface to make all properties optional
export interface JobFilters {
  remote?: boolean;
  fullTime?: boolean;
  location?: string;
  jobType?: string;
  experience?: string;
  salary?: string;
}

export const useJobMatching = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [jobMatchResults, setJobMatchResults] = useState<Job[]>([]);
  const [savedJobs, setSavedJobs] = useState<string[]>([]);
  const [activeFilters, setActiveFilters] = useState<JobFilters>({});
  const [searchHistory, setSearchHistory] = useState<any[]>([]);
  const [matchQuality, setMatchQuality] = useState<number | null>(null);
  const [jobCategories, setJobCategories] = useState<string[] | null>(null);
  const [appliedJobs, setAppliedJobs] = useState<string[]>([]);

  const handleFindMatches = async (searchQuery: string = "Software Engineer") => {
    setIsAnalyzing(true);
    setJobMatchResults([]);

    // Simulate job matching analysis (replace with actual API call)
    await new Promise(resolve => setTimeout(resolve, 1500));

    const mockResults: Job[] = [
      {
        id: '1',
        title: 'Software Engineer',
        company: 'Tech Corp',
        location: 'San Francisco, CA',
        salary: '$120,000 - $150,000',
        description: 'Develop and maintain web applications.',
        requirements: ['3+ years of experience', 'React', 'Node.js'],
        postedDate: '2024-01-01',
        type: 'Full-time',
        experience: 'Mid-level',
        companySize: '500-1000',
        industry: 'Technology',
        skills: ['React', 'Node.js', 'JavaScript', 'TypeScript'],
        matchScore: 85,
        hours: '40 hrs/week',
        posted: '2024-01-01',
      },
      {
        id: '2',
        title: 'Frontend Developer',
        company: 'Web Solutions',
        location: 'New York, NY',
        salary: '$110,000 - $140,000',
        description: 'Build user interfaces for web applications.',
        requirements: ['2+ years of experience', 'React', 'Redux'],
        postedDate: '2024-01-05',
        type: 'Full-time',
        experience: 'Mid-level',
        companySize: '200-500',
        industry: 'Technology',
        skills: ['React', 'Redux', 'JavaScript', 'HTML', 'CSS'],
        matchScore: 78,
        hours: '40 hrs/week',
        posted: '2024-01-05',
      },
      {
        id: '3',
        title: 'Backend Developer',
        company: 'Data Systems',
        location: 'Seattle, WA',
        salary: '$130,000 - $160,000',
        description: 'Design and implement backend systems.',
        requirements: ['5+ years of experience', 'Node.js', 'PostgreSQL'],
        postedDate: '2024-01-10',
        type: 'Full-time',
        experience: 'Senior-level',
        companySize: '1000+',
        industry: 'Technology',
        skills: ['Node.js', 'PostgreSQL', 'JavaScript', 'AWS'],
        matchScore: 92,
        hours: '40 hrs/week',
        posted: '2024-01-10',
      },
    ];

    setJobMatchResults(mockResults);
    setIsAnalyzing(false);

    // Simulate AI analysis to determine match quality and top categories
    setMatchQuality(82);
    setJobCategories(['Software Development', 'Web Development', 'Backend Engineering']);

    // Save search to history
    setSearchHistory(prevHistory => [
      {
        query: searchQuery,
        date: new Date().toLocaleDateString(),
        results: mockResults.length,
      },
      ...prevHistory,
    ]);
  };

  const toggleSaveJob = (jobId: string) => {
    setSavedJobs(prevSavedJobs => {
      if (prevSavedJobs.includes(jobId)) {
        return prevSavedJobs.filter(id => id !== jobId);
      } else {
        return [...prevSavedJobs, jobId];
      }
    });
  };

  const applyToJob = (jobId: string) => {
    setAppliedJobs(prev => [...prev, jobId]);
  };

  // Fix the setJobAlert function to only accept one parameter
  const setJobAlert = (jobId: string) => {
    // Mock implementation
    console.log(`Set job alert for job ${jobId}`);
  };

  const isSaved = (jobId: string | number) => savedJobs.includes(String(jobId));

  const updateFilters = (filters: JobFilters) => {
    setActiveFilters(filters);
  };

  const clearResults = () => {
    setJobMatchResults([]);
    setMatchQuality(null);
    setJobCategories(null);
  };

  return {
    isAnalyzing,
    jobMatchResults,
    handleFindMatches,
    savedJobs,
    toggleSaveJob,
    activeFilters,
    updateFilters,
    isSaved,
    clearResults,
    searchHistory,
    matchQuality,
    jobCategories,
    appliedJobs,
    applyToJob,
    setJobAlert
  };
};
